import React, { Component, ReactNode } from 'react';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import Tracker from '../../domain/tracker/Tracker';
import { query } from '@trlogic/client-js';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import ComponentName from '../../constant/ComponentName';
import ListDraggableItem from '../custom/ListDraggableItem';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ItemStatusComponent from '../ItemStatusComponent';
import moment from 'moment/moment';
import RoleHelper from '../../RoleHelper';
import ComponentUtil from '../../util/ComponentUtil';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import LanguageUtil from '../../util/LanguageUtil';
import { Tooltip } from 'primereact/tooltip';
import Icon from '../../icons/Icon';
import TrackerService from '../../service/TrackerService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import Order = query.Order;

class TrackerListViewerComponent extends Component<Props> {
  private readonly trackerService: TrackerService = ServiceContainer.getInstance().get(ServiceNames.TRACKER);

  public render() {
    const {
      views,
      trackers,
      loading,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && trackers && trackers.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={trackers}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          componentName={ComponentName.TRACKER}
          openNewTab={true}
          loading={loading}
          onRowClick={this.openEditor}
          onSelectionChange={this.onSelectionChange}
          columns={this.getColumns()}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.TRACKER} currentModal={this.props.currentModal} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.TRACKER} />
    );
  }

  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openTracker } = this.props;
    const name = e.data.name;
    openTracker(name);
  };

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.tracker.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[16%]',
        copyable: true,
        onCopy: (e: Tracker) => {
          return e.name;
        },
        body: (record: Tracker) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return view ? (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={30} text={record.name} />
            </div>
          ) : null;
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: 'pages.tracker.table.status', defaultMessage: 'Status' }),
        className: 'w-1/8',
        body: (record: Tracker) => {
          return <ItemStatusComponent status={record.stage} containerClassName={'justify-start'} />;
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.tracker.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[15%]',
        body: (record: Tracker) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.description} clipSize={30} showEllipsis={true} />
            </div>
          );
        },
      },
      {
        field: 'channel',
        header: LanguageUtil.get({ id: 'pages.tracker.table.channel', defaultMessage: 'Channel' }),
        className: 'min-w-[18%]',
        body: (record: Tracker) => {
          return <CustomTableContentWrapper text={record.event.name} clipSize={35} showEllipsis={true} />;
        },
      },
      {
        field: 'platform',
        header: LanguageUtil.get({ id: 'pages.tracker.table.platform', defaultMessage: 'Platform' }),
        body: (record: Tracker) => {
          return <CustomTableContentWrapper text={record.platform} />;
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.tracker.table.createdAt', defaultMessage: 'Created At' }),
        className: 'min-w-[11%]',
        body: (record: Tracker) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: 'pages.tracker.table.updatedAt', defaultMessage: 'Updated At' }),
        className: 'min-w-[11%]',
        body: (record: Tracker) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Tracker')) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.tracker.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: Tracker) => {
    const { permissions } = this.props;
    const actions: ReactNode[] = [];
    const permission = permissions[record.name];
    const allowed = ['Edit', 'Admin'];

    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.TRACKER, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => confirmUndeployWithImpact(ComponentName.TRACKER, [record.name], () => this.trackerService.unDeployTracker(record)),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.trackerService.removeTracker(record),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.trackerService.deployTracker(record),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );
    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };
}

interface Props extends ReduxProps {
  trackers: Tracker[];
  openTracker: (id: string) => void;
  currentModal?: any;
  views: any;
  loading: boolean;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TrackerListViewerComponent);
